/**
 * MemberBadge.tsx
 *
 * The "Member / Non-Member" pill shown next to a patient's name. Extracted from
 * the patient-details header so every surface that shows it — patient details,
 * the unified appointment modal — renders the same badge from the same rule.
 *
 * Note this is the ENROLLMENT-derived rule (what /memberships/patients/{id}
 * returns), which is not the same question as either:
 *   - `patientIsMember` on appointment payloads / the "m" badge on the
 *     appointments list, which follows legacy's member-record ledger and backs
 *     the member/non-member filter; or
 *   - `isMember` on the appointment-products response, which asks whether
 *     member PRICING applies today.
 * They agree for ordinary members and can differ for declined or lapsed ones.
 */
import { CreditCard } from 'lucide-react';
import { usePatientMemberships } from '@/hooks/usePatientDetail';
import type { PatientSidebarMembership } from '@/lib/patientDetail';
import { cn } from '@/lib/utils';

/**
 * A patient counts as a member when they hold an enrollment that is still
 * running: not ended (`incrementType !== '0'`) or ended but inside its
 * paid-through period ('cancelling'), and neither declined nor cancelled.
 */
export function isMemberFromMemberships(memberships: PatientSidebarMembership[]): boolean {
  return memberships.some(
    (m) => (m.incrementType !== '0' || m.section === 'cancelling') && !m.declined && m.statusText !== 'Cancelled',
  );
}

export function MemberBadge({ isMember, className }: { isMember: boolean; className?: string }) {
  return (
    <span
      className={cn(
        'inline-flex items-center gap-1 whitespace-nowrap rounded-full border px-2 py-0.5 text-[11px] font-medium',
        isMember
          ? 'border-emerald-200 bg-emerald-50 text-emerald-700'
          : 'border-slate-200 bg-slate-50 text-slate-500',
        className,
      )}
    >
      <CreditCard className="h-3 w-3" />
      {isMember ? 'Member' : 'Non-Member'}
    </span>
  );
}

/**
 * Self-fetching variant for callers that only hold a patient id. The query is
 * the same react-query key the patient sidebar uses, so opening this alongside
 * patient details costs no extra request.
 *
 * Renders nothing until the memberships are in — a real member would otherwise
 * read "Non-Member" for the first frames.
 */
export function PatientMemberBadge({
  patientId,
  className,
}: {
  patientId: number | string | null | undefined;
  className?: string;
}) {
  const { data: memberships, isLoading, isError } = usePatientMemberships(
    patientId ? String(patientId) : null,
  );

  if (!patientId || isLoading || isError) return null;

  return <MemberBadge isMember={isMemberFromMemberships(memberships)} className={className} />;
}
